/**
 * The sound a new order makes — §9.110, FB-205.
 *
 * <p>Nothing in the manager played audio at all. Legacy plays a *"3× fast double-beep for web/pending
 * orders"* behind a *Zvuk ZAP / VYP* toggle, and a web order nobody has accepted otherwise arrives in
 * silence — the dispatcher learns about it when they next look up.
 *
 * <p><b>Two patterns, because there are two kinds of arrival.</b> `arrivalsSince` already separates an
 * order **nobody has looked at** from one already in hand, and the boards already draw them as two
 * different toasts. `urgent` is the insistent one; `gentle` is a single note that says *something
 * landed* without demanding the room's attention.
 *
 * <p><b>Off by default — the owner's decision, and a divergence from legacy.</b> A tablet in a quiet
 * dining room must not start beeping because somebody deployed. It is also what the browser wants:
 * `AudioContext` stays suspended until the page has had a user gesture, so a switch somebody presses
 * *is* that gesture, where an always-on default would be a promise the browser silently breaks.
 *
 * <p><b>Synthesised, not a file.</b> Legacy oscillates two square waves; copying that keeps the sound
 * the staff already know and adds no asset to load, no request that can 404 on a tablet with no
 * signal.
 */

/** What kind of arrival this is. */
export type ChimeKind = 'urgent' | 'gentle'

/** One note: when it starts relative to the chime, how long it lasts, and its pitch. */
export interface ChimeTone {
  /** Seconds from the start of the chime. */
  at: number
  /** Seconds. */
  duration: number
  hertz: number
}

const URGENT_OFFSETS = [0, 0.22, 0.44]
const URGENT_PITCHES = [880, 1100]

/**
 * The notes, as legacy sounds them.
 *
 * <p>Three fast double-beeps for the urgent case — 880 Hz then 1100 Hz, repeated — and one soft note
 * otherwise. The pattern carries the meaning: somebody hearing it from across a kitchen should know
 * which kind it was without looking.
 */
export function chimeTones(kind: ChimeKind): ChimeTone[] {
  if (kind === 'gentle') {
    return [{ at: 0, duration: 0.18, hertz: 660 }]
  }
  return URGENT_OFFSETS.flatMap((offset) =>
    URGENT_PITCHES.map((hertz, index) => ({
      at: offset + index * 0.08,
      duration: 0.07,
      hertz,
    })),
  )
}

/** How long the whole chime runs, so a caller can avoid stacking two on top of each other. */
export function chimeDurationSeconds(kind: ChimeKind): number {
  return chimeTones(kind).reduce((end, tone) => Math.max(end, tone.at + tone.duration), 0)
}

/** Where the toggle's state lives. Per browser, because it is about this device's room. */
export const CHIME_STORAGE_KEY = 'agos_arrival_sound'

/**
 * Whether the chime is on.
 *
 * <p><b>Anything that is not exactly `'on'` is off</b>, including a missing key, a corrupted value,
 * and the first visit. The safe direction for a setting about noise is silence: somebody who wants
 * sound will press the button, and somebody who does not should never have to discover why the tablet
 * is beeping.
 */
export function chimeEnabled(stored: string | null | undefined): boolean {
  return stored === 'on'
}

export function storedChimeValue(enabled: boolean): string {
  return enabled ? 'on' : 'off'
}

/** What the toggle says it will do next. */
export function chimeToggleLabel(enabled: boolean): string {
  return enabled ? 'Zvuk ZAP' : 'Zvuk VYP'
}

/**
 * Which chime an arrival deserves, if any.
 *
 * <p>Pending wins when both kinds arrived at once: the unaccepted order is the one that needs
 * somebody now, and two chimes over each other is noise rather than information.
 */
export function chimeFor(counts: { pending: number; accepted: number }): ChimeKind | null {
  if (counts.pending > 0) return 'urgent'
  if (counts.accepted > 0) return 'gentle'
  return null
}
